import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Image, Music, Video, Mic, FolderOpen, ArrowLeft, MapPin, Users, Clapperboard, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import HelpDot from '@/components/ui/HelpDot';

interface Asset {
  name: string;
  path: string;
  type: 'image' | 'video' | 'audio' | 'mixed';
}

interface AssetFolder {
  id: string;
  /** Translation key in the `production` namespace, resolved at render time. */
  labelKey: string;
  icon: React.ReactNode;
  assets: Asset[];
  rejected?: Asset[];
}

interface StoryboardKeyframe {
  shot_id: string;
  scene_number: number;
  roll: string;
  roll_label: string;
  shot_type: string;
  sequence_order: number;
  path: string;
}

interface AssetBrowserProps {
  productionId: string;
  availableAssets: string[];
  onPromoteCharacter?: (characterName: string) => void;
}

const FOLDER_CONFIG: {
  prefix: string;
  id: string;
  labelKey: string;
  icon: React.ReactNode;
  type: 'image' | 'video' | 'audio' | 'mixed';
}[] = [
  {
    prefix: 'auditions/',
    id: 'auditions',
    labelKey: 'browser.folder.voiceAuditions',
    icon: <Mic className="h-4 w-4" />,
    type: 'audio',
  },
  {
    prefix: 'audio/vo_booth/',
    id: 'vo_booth',
    labelKey: 'browser.folder.voBooth',
    icon: <Mic className="h-4 w-4" />,
    type: 'video',
  },
  {
    prefix: 'audio/dialog_vo/',
    id: 'dialog_vo',
    labelKey: 'browser.folder.dialogAlternates',
    icon: <Mic className="h-4 w-4" />,
    type: 'audio',
  },
  {
    prefix: 'characters/',
    id: 'characters',
    labelKey: 'browser.folder.characters',
    icon: <Users className="h-4 w-4" />,
    type: 'image',
  },
  {
    prefix: 'locations/',
    id: 'locations',
    labelKey: 'browser.folder.locations',
    icon: <MapPin className="h-4 w-4" />,
    type: 'image',
  },
  {
    prefix: 'keyframes/',
    id: 'keyframes',
    labelKey: 'browser.folder.storyboard',
    icon: <Image className="h-4 w-4" />,
    type: 'image',
  },
  { prefix: 'clips/', id: 'clips', labelKey: 'browser.folder.clips', icon: <Video className="h-4 w-4" />, type: 'video' },
  { prefix: 'audio/vo/', id: 'vo', labelKey: 'browser.folder.voiceover', icon: <Mic className="h-4 w-4" />, type: 'audio' },
  {
    prefix: 'audio/dialog/',
    id: 'dialog',
    labelKey: 'browser.folder.dialog',
    icon: <Mic className="h-4 w-4" />,
    type: 'audio',
  },
  { prefix: 'audio/music/', id: 'music', labelKey: 'browser.folder.music', icon: <Music className="h-4 w-4" />, type: 'audio' },
  {
    prefix: 'audio/sfx/',
    id: 'sfx',
    labelKey: 'browser.folder.sfx',
    icon: <Music className="h-4 w-4" />,
    type: 'audio',
  },
  {
    prefix: 'final/',
    id: 'final',
    labelKey: 'browser.folder.final',
    icon: <Clapperboard className="h-4 w-4" />,
    type: 'mixed',
  },
];

function buildFolders(productionId: string, availableAssets: string[]): AssetFolder[] {
  const folders: AssetFolder[] = [];

  for (const config of FOLDER_CONFIG) {
    const matching = availableAssets.filter(
      (a) => a.startsWith(config.prefix) && !a.endsWith('.json') && !a.includes('/rejected/'),
    );
    const rejectedMatching = availableAssets.filter(
      (a) => a.startsWith(config.prefix) && !a.endsWith('.json') && a.includes('/rejected/'),
    );
    if (matching.length === 0) continue;

    folders.push({
      id: config.id,
      labelKey: config.labelKey,
      icon: config.icon,
      assets: matching.map((a) => ({
        name: a.replace(config.prefix, ''),
        path: `/api/assets/${productionId}/${a}`,
        type: config.type,
      })),
      rejected: rejectedMatching.map((a) => ({
        name: a.replace(config.prefix, ''),
        path: `/api/assets/${productionId}/${a}`,
        type: config.type,
      })),
    });
  }

  // JSON/metadata files
  const jsonFiles = availableAssets.filter((a) => a.endsWith('.json'));
  if (jsonFiles.length > 0) {
    folders.push({
      id: 'metadata',
      labelKey: 'browser.folder.metadata',
      icon: <FileText className="h-4 w-4" />,
      assets: jsonFiles.map((a) => ({
        name: a,
        path: `/api/assets/${productionId}/${a}`,
        type: 'image' as const,
      })),
    });
  }

  return folders;
}

// ADR status → a small tile badge. revoiced = dialogue re-voiced to the cast
// voice; muted = B-roll audio stripped; kept = ADR left the original (flag);
// failed = ffmpeg/ADR error. null = no ADR record for this shot.
function adrBadge(status: string | undefined): { label: string; cls: string } | null {
  switch (status) {
    case 'revoiced':
      return { label: 'ADR ✓', cls: 'bg-emerald-600/90 text-white' };
    case 'muted':
      return { label: 'Muted', cls: 'bg-slate-600/90 text-white' };
    case 'kept':
      return { label: 'ADR: kept', cls: 'bg-amber-500/90 text-black' };
    case 'failed':
      return { label: 'ADR: failed', cls: 'bg-red-600/90 text-white' };
    default:
      return null;
  }
}

// Map a clip filename to its shot_id (strip extension + chained-segment suffix),
// so segment tiles (S01_SH01_seg2.mp4) resolve to the shot's ADR record.
function shotIdOf(name: string): string {
  return name.replace('.mp4', '').replace(/_seg\d+$/, '');
}

function formatDuration(sec: number): string {
  if (!isFinite(sec) || sec <= 0) return '';
  return sec >= 60 ? `${Math.floor(sec / 60)}:${String(Math.round(sec % 60)).padStart(2, '0')}` : `${sec.toFixed(1)}s`;
}

export default function AssetBrowser({ productionId, availableAssets, onPromoteCharacter }: AssetBrowserProps) {
  const { t } = useTranslation('production');
  const [activeFolder, setActiveFolder] = useState<string | null>(null);
  const [enlarged, setEnlarged] = useState<string | null>(null);
  const [storyboardMeta, setStoryboardMeta] = useState<Record<string, StoryboardKeyframe>>({});
  const [adrStatus, setAdrStatus] = useState<Record<string, string>>({});
  const [clipDurations, setClipDurations] = useState<Record<string, number>>({});
  const [feedbackAsset, setFeedbackAsset] = useState<string | null>(null);
  const [feedbackNote, setFeedbackNote] = useState('');
  const [feedbackSending, setFeedbackSending] = useState(false);
  const [showRejectedTakes, setShowRejectedTakes] = useState(false);

  const submitFeedback = async (assetName: string) => {
    if (!feedbackNote.trim()) return;
    setFeedbackSending(true);
    try {
      const { apiFetch } = await import('@/lib/api');
      await apiFetch(`/api/feedback/${productionId}`, {
        method: 'POST',
        body: JSON.stringify({ asset: assetName, note: feedbackNote }),
      });
      setFeedbackAsset(null);
      setFeedbackNote('');
    } catch {
      // best effort
    } finally {
      setFeedbackSending(false);
    }
  };

  useEffect(() => {
    // Load storyboard metadata for keyframe roll labels
    if (availableAssets.includes('storyboard.json')) {
      fetch(`/api/assets/${productionId}/storyboard.json`)
        .then((r) => r.json())
        .then((data) => {
          const map: Record<string, StoryboardKeyframe> = {};
          for (const kf of data.keyframes || []) {
            map[`${kf.shot_id}.png`] = kf;
            map[`${kf.shot_id}.mp4`] = kf;
          }
          setStoryboardMeta((prev) => ({ ...prev, ...map }));
        })
        .catch(() => {});
    }
    // Also load shot_plan for roll info on clips
    if (availableAssets.includes('shot_plan.json')) {
      fetch(`/api/assets/${productionId}/shot_plan.json`)
        .then((r) => r.json())
        .then((data) => {
          const map: Record<string, StoryboardKeyframe> = {};
          for (const shot of data.shots || []) {
            const roll = shot.roll || 'a_roll';
            const entry = {
              shot_id: shot.shot_id,
              scene_number: shot.scene_number || 0,
              roll,
              roll_label: roll === 'a_roll' ? 'A-Roll' : 'B-Roll',
              shot_type: shot.shot_type || '',
              sequence_order: 0,
              path: '',
            };
            map[`${shot.shot_id}.png`] = entry;
            map[`${shot.shot_id}.mp4`] = entry;
          }
          setStoryboardMeta((prev) => ({ ...prev, ...map }));
        })
        .catch(() => {});
    }
    // Load ADR manifest so each clip tile can show whether ADR was applied
    // (revoiced dialogue / muted B-roll / kept-original). Keyed by shot_id.
    if (availableAssets.includes('adr_manifest.json')) {
      fetch(`/api/assets/${productionId}/adr_manifest.json`)
        .then((r) => r.json())
        .then((data) => {
          const map: Record<string, string> = {};
          for (const [shotId, info] of Object.entries(data || {})) {
            map[shotId] = (info as { status?: string })?.status || '';
          }
          setAdrStatus(map);
        })
        .catch(() => {});
    }
  }, [productionId, availableAssets]);

  const folders = buildFolders(productionId, availableAssets);
  const totalAssets = folders.reduce((sum, f) => sum + f.assets.length, 0);
  const currentFolder = folders.find((f) => f.id === activeFolder);

  if (totalAssets === 0) return null;

  return (
    <>
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-sm font-medium">
              <FolderOpen className="h-4 w-4" />
              {activeFolder ? (
                <>
                  <Button variant="ghost" size="sm" className="h-6 px-2" onClick={() => setActiveFolder(null)}>
                    <ArrowLeft className="mr-1 h-3 w-3" />
                    {t('browser.backToAssets')}
                  </Button>
                  <span>/ {currentFolder ? t(currentFolder.labelKey) : ''}</span>
                </>
              ) : (
                <>
                  {t('browser.titleCount', { count: totalAssets })}
                  <HelpDot>{t('browser.help')}</HelpDot>
                </>
              )}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {!activeFolder ? (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
              {folders.map((folder) => (
                <button
                  key={folder.id}
                  className={[
                    'flex flex-col items-center gap-1.5 p-3 rounded-lg',
                    'border border-border hover:bg-muted/50',
                    'hover:border-primary/30 transition-colors cursor-pointer text-center',
                  ].join(' ')}
                  onClick={() => setActiveFolder(folder.id)}
                >
                  <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center">{folder.icon}</div>
                  <span className="text-xs font-medium">{t(folder.labelKey)}</span>
                  <Badge variant="secondary" className="text-[10px]">
                    {folder.assets.length}
                  </Badge>
                </button>
              ))}
            </div>
          ) : currentFolder ? (
            <div>
              {currentFolder.assets[0]?.type === 'image' && !currentFolder.id.includes('metadata') ? (
                <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                  {currentFolder.assets.map((asset) => {
                    const meta = currentFolder.id === 'keyframes' ? storyboardMeta[asset.name] : null;
                    const rollLabel = meta?.roll_label;
                    const shotType = meta?.shot_type;
                    return (
                      <div key={asset.name} className="relative group cursor-pointer" onClick={() => setEnlarged(asset.path)}>
                        <img
                          src={asset.path}
                          alt={asset.name}
                          className="w-full aspect-video object-cover rounded border border-border group-hover:border-primary transition-colors"
                        />
                        {rollLabel && (
                          <div
                            className={`absolute top-1 right-1 text-[9px] font-bold px-1.5 py-0.5 rounded ${
                              meta.roll === 'a_roll' ? 'bg-blue-600/90 text-white' : 'bg-amber-500/90 text-black'
                            }`}
                          >
                            {rollLabel}
                          </div>
                        )}
                        <button
                          className={[
                            'absolute top-1 left-1 opacity-0',
                            'group-hover:opacity-100 transition-opacity',
                            'bg-yellow-500/90 text-black text-[9px]',
                            'font-bold px-1.5 py-0.5 rounded',
                          ].join(' ')}
                          onClick={(e) => {
                            e.stopPropagation();
                            setFeedbackAsset(asset.name);
                          }}
                        >
                          Note
                        </button>
                        {currentFolder.id === 'characters' && onPromoteCharacter && (
                          <button
                            className={[
                              'absolute top-1 right-1 opacity-0',
                              'group-hover:opacity-100 transition-opacity',
                              'bg-emerald-600/90 text-white text-[9px]',
                              'font-bold px-1.5 py-0.5 rounded',
                            ].join(' ')}
                            title={t('browser.lockCharacter')}
                            onClick={(e) => {
                              e.stopPropagation();
                              onPromoteCharacter(asset.name.replace('.png', ''));
                            }}
                          >
                            ★ Project
                          </button>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] px-1 py-0.5 rounded-b truncate">
                          {asset.name.replace('.png', '')}
                          {shotType ? ` • ${shotType}` : ''}
                          {rollLabel ? ` • ${rollLabel}` : ''}
                        </div>
                        {feedbackAsset === asset.name && (
                          <div
                            className="absolute inset-0 bg-black/80 rounded flex flex-col p-2 z-10"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <textarea
                              className={[
                                'flex-1 bg-white/10 text-white text-[11px]',
                                'p-1 rounded resize-none border',
                                'border-white/20 placeholder:text-white/40',
                              ].join(' ')}
                              placeholder={t('browser.notePlaceholder')}
                              value={feedbackNote}
                              onChange={(e) => setFeedbackNote(e.target.value)}
                              autoFocus
                            />
                            <div className="flex gap-1 mt-1">
                              <button
                                className="flex-1 bg-yellow-500 text-black text-[10px] font-bold py-0.5 rounded"
                                onClick={() => submitFeedback(asset.name)}
                                disabled={feedbackSending}
                              >
                                {feedbackSending ? '...' : 'Send'}
                              </button>
                              <button
                                className="px-2 bg-white/10 text-white text-[10px] py-0.5 rounded"
                                onClick={() => {
                                  setFeedbackAsset(null);
                                  setFeedbackNote('');
                                }}
                              >
                                X
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : currentFolder.assets[0]?.type === 'video' ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {currentFolder.assets.map((asset) => {
                    const clipMeta = storyboardMeta[asset.name];
                    const clipRoll = clipMeta?.roll_label;
                    const clipType = clipMeta?.shot_type;
                    const adr = adrBadge(adrStatus[shotIdOf(asset.name)]);
                    const dur = clipDurations[asset.name];
                    const durLabel = dur ? formatDuration(dur) : '';
                    return (
                      <div key={asset.name} className="relative group">
                        <video
                          src={asset.path}
                          className="w-full aspect-video rounded border border-border bg-black"
                          controls
                          playsInline
                          onLoadedMetadata={(e) => {
                            const d = (e.target as HTMLVideoElement).duration;
                            if (isFinite(d) && d > 0) {
                              setClipDurations((prev) => (prev[asset.name] === d ? prev : { ...prev, [asset.name]: d }));
                            }
                          }}
                        />
                        {clipRoll && (
                          <div
                            className={[
                              'absolute top-1 right-1 text-[9px] font-bold',
                              'px-1.5 py-0.5 rounded',
                              clipMeta?.roll === 'a_roll' ? 'bg-blue-600/90 text-white' : 'bg-amber-500/90 text-black',
                            ].join(' ')}
                          >
                            {clipRoll}
                          </div>
                        )}
                        {adr && (
                          <div
                            className={['absolute top-1 left-1 text-[9px] font-bold px-1.5 py-0.5 rounded', adr.cls].join(' ')}
                          >
                            {adr.label}
                          </div>
                        )}
                        {durLabel && (
                          <div className="absolute bottom-9 right-1 text-[9px] font-bold px-1.5 py-0.5 rounded bg-black/70 text-white">
                            {durLabel}
                          </div>
                        )}
                        <div className="text-[10px] text-muted-foreground mt-0.5 truncate">
                          {asset.name.replace('.mp4', '')}
                          {clipType ? ` • ${clipType}` : ''}
                          {clipRoll ? ` • ${clipRoll}` : ''}
                          {durLabel ? ` • ${durLabel}` : ''}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : currentFolder.assets[0]?.type === 'audio' ? (
                <div className="space-y-2">
                  {currentFolder.assets.map((asset) => (
                    <div key={asset.name} className="relative group">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium w-32 truncate">{asset.name}</span>
                        <audio src={asset.path} controls className="h-7 flex-1" />
                        <button
                          className={[
                            'opacity-0 group-hover:opacity-100',
                            'transition-opacity text-[9px] font-bold',
                            'bg-yellow-500/90 text-black',
                            'px-1.5 py-0.5 rounded flex-shrink-0',
                          ].join(' ')}
                          onClick={() => setFeedbackAsset(asset.name)}
                        >
                          Note
                        </button>
                      </div>
                      {feedbackAsset === asset.name && (
                        <div className="mt-1 flex gap-1">
                          <textarea
                            className={[
                              'flex-1 bg-muted text-xs p-1.5 rounded',
                              'resize-none border border-border',
                              'placeholder:text-muted-foreground',
                            ].join(' ')}
                            placeholder={t('browser.notePlaceholder')}
                            value={feedbackNote}
                            onChange={(e) => setFeedbackNote(e.target.value)}
                            rows={2}
                            autoFocus
                          />
                          <div className="flex flex-col gap-1">
                            <button
                              className="bg-yellow-500 text-black text-[10px] font-bold px-2 py-1 rounded"
                              onClick={() => submitFeedback(asset.name)}
                              disabled={feedbackSending}
                            >
                              {feedbackSending ? '...' : 'Send'}
                            </button>
                            <button
                              className="bg-muted text-xs px-2 py-1 rounded"
                              onClick={() => {
                                setFeedbackAsset(null);
                                setFeedbackNote('');
                              }}
                            >
                              X
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : currentFolder.assets[0]?.type === 'mixed' ? (
                <div className="space-y-3">
                  {currentFolder.assets
                    .filter((a) => a.name.endsWith('.mp4'))
                    .map((asset) => (
                      <div key={asset.name} className="relative">
                        <video
                          src={asset.path}
                          className="w-full aspect-video rounded border border-border bg-black"
                          controls
                          playsInline
                        />
                        <div className="text-xs text-muted-foreground mt-1">{asset.name}</div>
                      </div>
                    ))}
                  {currentFolder.assets.filter((a) => !a.name.endsWith('.mp4')).length > 0 && (
                    <div className="border-t pt-2 space-y-1.5">
                      <div className="text-xs font-medium text-muted-foreground">Export Files</div>
                      {currentFolder.assets
                        .filter((a) => !a.name.endsWith('.mp4'))
                        .map((asset) => (
                          <a
                            key={asset.name}
                            href={asset.path}
                            download={asset.name}
                            className={`flex items-center gap-2 text-xs p-1.5 rounded
                              hover:bg-muted/50 text-primary underline-offset-2
                              hover:underline`}
                          >
                            <FileText className="h-3.5 w-3.5" />
                            <span>{asset.name}</span>
                            <Badge variant="outline" className="text-[9px] ml-auto">
                              {asset.name.split('.').pop()?.toUpperCase()}
                            </Badge>
                          </a>
                        ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-1">
                  {currentFolder.assets.map((asset) => (
                    <div
                      key={asset.name}
                      className="flex items-center gap-2 text-xs text-muted-foreground p-1 rounded hover:bg-muted/50"
                    >
                      <FileText className="h-3 w-3" />
                      <span>{asset.name}</span>
                    </div>
                  ))}
                </div>
              )}
              {(currentFolder.rejected?.length ?? 0) > 0 && (
                <div className="mt-4 border-t border-border pt-2">
                  <button
                    className="text-xs font-medium text-muted-foreground flex items-center gap-1 cursor-pointer"
                    onClick={() => setShowRejectedTakes((s) => !s)}
                  >
                    <span className="text-[10px]">{showRejectedTakes ? '▼' : '▶'}</span>
                    Rejected takes ({currentFolder.rejected!.length})
                    <span className="font-normal">— prior attempts; stacked as versions in Iconik</span>
                  </button>
                  {showRejectedTakes && (
                    <div className="grid grid-cols-4 gap-2 mt-2 opacity-75">
                      {currentFolder.rejected!.map((asset) =>
                        asset.type === 'video' ? (
                          <div key={asset.name} className="relative">
                            <video
                              src={asset.path}
                              className="w-full aspect-video rounded border border-border bg-black"
                              controls
                              playsInline
                              preload="none"
                            />
                            <div className="text-[9px] text-muted-foreground truncate">{asset.name}</div>
                          </div>
                        ) : (
                          <div key={asset.name} className="relative">
                            <img
                              src={`${asset.path}?thumb=1`}
                              alt={asset.name}
                              loading="lazy"
                              className="w-full aspect-video object-cover rounded border border-border"
                            />
                            <div className="text-[9px] text-muted-foreground truncate">{asset.name}</div>
                          </div>
                        ),
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {enlarged && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-8 cursor-pointer"
          onClick={() => setEnlarged(null)}
        >
          <div className="relative max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={enlarged} alt="" className="w-full rounded-lg" />
            <button
              onClick={() => setEnlarged(null)}
              className="absolute top-4 right-4 bg-black/70 text-white w-8 h-8 rounded-full flex items-center justify-center hover:bg-black/90"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </>
  );
}
